/**
 * SILO Wheel popup — spin-to-win email capture.
 * Self-contained custom element: no dependency on the theme's Modal/main.js globals,
 * so the component ports across Enterprise theme versions unchanged.
 *
 * Flow: open -> email submit (native customer form via fetch) -> spin -> result.
 * The winning segment is picked BEFORE the animation; the animation lands on it.
 */
(() => {
  if (customElements.get('sd-wheel-popup')) return;

  const STORAGE_PREFIX = 'cc-sd-wheel:';
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  const DESKTOP_MQ = window.matchMedia('(min-width: 769px)');
  let liveInstance = null;

  /* Storage helper: localStorage throws in some private modes — degrade to memory. */
  const memoryStore = {};
  const storage = {
    get(key) {
      try {
        const raw = window.localStorage.getItem(STORAGE_PREFIX + key);
        return raw ? JSON.parse(raw) : memoryStore[key] || null;
      } catch (e) {
        return memoryStore[key] || null;
      }
    },
    set(key, value) {
      memoryStore[key] = value;
      try {
        window.localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(value));
      } catch (e) { /* memory fallback already holds it */ }
    },
    session(key, value) {
      try {
        if (arguments.length === 2) {
          if (value === null) window.sessionStorage.removeItem(STORAGE_PREFIX + key);
          else window.sessionStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(value));
          return value;
        }
        const raw = window.sessionStorage.getItem(STORAGE_PREFIX + key);
        return raw ? JSON.parse(raw) : null;
      } catch (e) {
        return null;
      }
    }
  };

  const luminance = (hex) => {
    const h = (hex || '').replace('#', '');
    if (h.length < 6) return 0;
    const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.substr(i, 2), 16) / 255);
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };

  class SdWheelPopup extends HTMLElement {
    connectedCallback() {
      if (this.sdInit) return;
      this.sdInit = true;
      requestAnimationFrame(() => this.init());
    }

    disconnectedCallback() {
      clearTimeout(this.delayTimer);
      clearTimeout(this.spinFallbackTimer);
      if (this.mouseLeaveHandler) document.body.removeEventListener('mouseleave', this.mouseLeaveHandler);
      if (this.scrollHandler) window.removeEventListener('scroll', this.scrollHandler);
      if (this.keydownHandler) document.removeEventListener('keydown', this.keydownHandler);
      if (this.designHandlers) {
        document.removeEventListener('shopify:section:select', this.designHandlers.select);
        document.removeEventListener('shopify:section:deselect', this.designHandlers.deselect);
        document.removeEventListener('shopify:block:select', this.designHandlers.block);
      }
      document.documentElement.classList.remove('cc-sd-wheel-lock');
      if (liveInstance === this) liveInstance = null;
    }

    init() {
      const configEl = this.querySelector('[data-sd-wheel-config]');
      if (!configEl) return;
      try {
        this.cfg = JSON.parse(configEl.textContent);
      } catch (e) {
        return;
      }
      if (!this.cfg.segments || !this.cfg.segments.length) return;

      this.overlay = this.querySelector('[data-sdw-overlay]');
      this.window = this.querySelector('.sd-wheel__window');
      this.wheelWrap = this.querySelector('[data-sdw-wheel]');
      this.form = this.querySelector('.sd-wheel__form');
      this.tagsInput = this.querySelector('[data-sdw-tags]');
      this.fieldset = this.querySelector('[data-sdw-fieldset]');
      this.errorEl = this.querySelector('[data-sdw-error]');
      this.liveEl = this.querySelector('[data-sdw-live]');
      this.teaser = this.querySelector('[data-sdw-teaser]');
      this.currentRotation = 0;
      this.designMode = window.Shopify && window.Shopify.designMode;

      this.buildWheel();
      this.bindUi();

      if (this.designMode) {
        this.bindDesignMode();
        return;
      }

      // One live instance only (multiple would fight over triggers + storage).
      if (liveInstance) {
        console.warn('sd-wheel-popup: another instance is already active; this one stays inert.');
        return;
      }
      liveInstance = this;

      if (!this.cfg.showOnMobile && !DESKTOP_MQ.matches) return;

      this.trackImpression();
      if (this.resumeFromChallenge()) return;

      const state = this.readState();
      if (state.won && state.won.code) {
        this.showTeaser();
        return;
      }
      const dismissedRecently = state.dismissedAt
        && (Date.now() - state.dismissedAt) < this.cfg.dismissDays * 864e5;
      if (dismissedRecently) {
        this.showTeaser();
        return;
      }
      if (!this.cfg.teaserAfterDismissOnly) this.showTeaser();
      this.armTriggers();
    }

    /* ---------- state + storage ---------- */

    readState() {
      return this.readStateRaw() || { v: 1, dismissedAt: 0, spins: 0, won: null };
    }

    readStateRaw() {
      return storage.get(this.cfg.sectionId);
    }

    writeState(patch) {
      storage.set(this.cfg.sectionId, Object.assign(this.readState(), patch));
    }

    setState(name) {
      this.dataset.state = name;
    }

    track(event, params) {
      if (!this.cfg.dataLayer || !Array.isArray(window.dataLayer)) return;
      window.dataLayer.push(Object.assign({ event, wheel_section: this.cfg.sectionId }, params));
    }

    trackImpression() {
      if (storage.session(`imp:${this.cfg.sectionId}`)) return;
      storage.session(`imp:${this.cfg.sectionId}`, 1);
      this.track('wheel_impression');
    }

    /* ---------- triggers ---------- */

    armTriggers() {
      this.delayTimer = setTimeout(() => this.openFromTrigger('delay'), this.cfg.delay * 1000);

      if (this.cfg.exitIntent && DESKTOP_MQ.matches) {
        this.mouseLeaveHandler = (evt) => {
          if (evt.clientY < 0) this.openFromTrigger('exit');
        };
        document.body.addEventListener('mouseleave', this.mouseLeaveHandler);
      }

      if (this.cfg.scrollTrigger) {
        this.scrollHandler = () => {
          const max = document.documentElement.scrollHeight - window.innerHeight;
          if (max > 0 && (window.scrollY / max) * 100 >= this.cfg.scrollDepth) {
            this.openFromTrigger('scroll');
          }
        };
        window.addEventListener('scroll', this.scrollHandler, { passive: true });
      }
    }

    disarmTriggers() {
      clearTimeout(this.delayTimer);
      if (this.mouseLeaveHandler) {
        document.body.removeEventListener('mouseleave', this.mouseLeaveHandler);
        this.mouseLeaveHandler = null;
      }
      if (this.scrollHandler) {
        window.removeEventListener('scroll', this.scrollHandler);
        this.scrollHandler = null;
      }
    }

    openFromTrigger(trigger) {
      this.disarmTriggers();
      this.open(null, trigger);
    }

    /* ---------- open / close ---------- */

    open(opener, trigger) {
      if (this.hasAttribute('open')) return;
      this.returnFocusTo = opener || document.activeElement;

      const state = this.designMode ? { spins: 0, won: null } : this.readState();
      if (state.won && state.won.code) {
        this.showResult(this.segmentByHandle(state.won.handle), state.won, true);
      } else if (state.spins >= this.cfg.maxSpins) {
        this.setState('capped');
      } else {
        this.setState('open');
      }

      this.setAttribute('open', '');
      document.documentElement.classList.add('cc-sd-wheel-lock');
      if (this.teaser) this.teaser.hidden = true;

      this.keydownHandler = (evt) => {
        if (evt.key === 'Escape' && this.dataset.state !== 'spinning') this.dismiss();
        if (evt.key === 'Tab') this.trapFocus(evt);
      };
      document.addEventListener('keydown', this.keydownHandler);

      const focusTarget = this.dataset.state === 'open'
        ? this.querySelector('.sd-wheel__input')
        : this.window;
      setTimeout(() => focusTarget && focusTarget.focus({ preventScroll: true }), 100);

      this.track('wheel_open', { wheel_trigger: trigger || 'manual' });
    }

    close() {
      this.removeAttribute('open');
      document.documentElement.classList.remove('cc-sd-wheel-lock');
      if (this.keydownHandler) {
        document.removeEventListener('keydown', this.keydownHandler);
        this.keydownHandler = null;
      }
      if (this.returnFocusTo && document.contains(this.returnFocusTo)) {
        this.returnFocusTo.focus({ preventScroll: true });
      }
    }

    dismiss() {
      if (this.dataset.state === 'spinning') return;
      this.close();
      if (!this.designMode) {
        const state = this.readState();
        if (!(state.won && state.won.code)) this.writeState({ dismissedAt: Date.now() });
        this.track('wheel_dismiss');
      }
      this.showTeaser();
    }

    showTeaser() {
      if (this.teaser) {
        this.teaser.hidden = false;
        this.returnFocusTo = this.teaser;
      }
    }

    trapFocus(evt) {
      const focusables = this.window.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      const visible = [...focusables].filter((el) => el.offsetParent !== null);
      if (!visible.length) return;
      const first = visible[0];
      const last = visible[visible.length - 1];
      if (evt.shiftKey && document.activeElement === first) {
        evt.preventDefault();
        last.focus();
      } else if (!evt.shiftKey && document.activeElement === last) {
        evt.preventDefault();
        first.focus();
      }
    }

    /* ---------- UI bindings ---------- */

    bindUi() {
      this.addEventListener('click', (evt) => {
        if (evt.target.closest('[data-sdw-close]')) return this.dismiss();
        if (evt.target.closest('[data-sdw-teaser]')) {
          return this.open(evt.target.closest('[data-sdw-teaser]'), 'teaser');
        }
        if (evt.target.closest('[data-sdw-copy]')) return this.copyCode();
        if (evt.target.closest('[data-sdw-retry]')) return this.setState('open');
        if (evt.target === this.overlay && this.dataset.state !== 'spinning') this.dismiss();
      });

      if (this.form) {
        this.form.addEventListener('submit', (evt) => this.handleSubmit(evt));
      }
    }

    bindDesignMode() {
      const section = this.closest('.shopify-section');
      this.designHandlers = {
        select: (evt) => {
          if (evt.target === section) {
            this.setState('open');
            this.open();
          }
        },
        deselect: (evt) => {
          if (evt.target === section) this.close();
        },
        block: (evt) => {
          if (!section.contains(evt.target)) return;
          const idx = this.cfg.segments.findIndex((s) => evt.detail && evt.detail.blockId === s.blockId);
          if (idx < 0) return;
          if (!this.hasAttribute('open')) this.open();
          this.setState('open');
          this.rotateToSegment(idx, true);
        }
      };
      document.addEventListener('shopify:section:select', this.designHandlers.select);
      document.addEventListener('shopify:section:deselect', this.designHandlers.deselect);
      document.addEventListener('shopify:block:select', this.designHandlers.block);
      this.showTeaser();
    }

    /* ---------- wheel rendering ---------- */

    segmentColor(i) {
      const seg = this.cfg.segments[i];
      if (this.cfg.paletteMode === 'custom' && seg.color) return seg.color;
      return this.cfg.palette[i % this.cfg.palette.length];
    }

    buildWheel() {
      const n = this.cfg.segments.length;
      const theta = 360 / n;
      const R = 48;
      const rad = (deg) => (deg * Math.PI) / 180;
      const labelList = this.cfg.segments.map((s) => s.label).join(', ');

      let wedges = '';
      for (let i = 0; i < n; i += 1) {
        const fill = this.segmentColor(i);
        const textFill = luminance(fill) > 0.55 ? '#1a1a1a' : '#ffffff';
        let path;
        if (n === 1) {
          path = `M0 -${R} A${R} ${R} 0 1 1 0 ${R} A${R} ${R} 0 1 1 0 -${R} Z`;
        } else {
          const largeArc = theta > 180 ? 1 : 0;
          const x = (R * Math.sin(rad(theta))).toFixed(3);
          const y = (-R * Math.cos(rad(theta))).toFixed(3);
          path = `M0 0 L0 -${R} A${R} ${R} 0 ${largeArc} 1 ${x} ${y} Z`;
        }
        const fontSize = Math.min(6, (30 / n) + 2).toFixed(2);
        const label = n <= 4
          ? `<text class="sd-wheel__seg-label" transform="rotate(${theta / 2}) translate(0 -${R * 0.62})" text-anchor="middle" fill="${textFill}" font-size="${fontSize}">${this.escapeHtml(this.cfg.segments[i].label)}</text>`
          : `<text class="sd-wheel__seg-label" transform="rotate(${theta / 2 - 90}) translate(${R * 0.9} 0)" text-anchor="end" dominant-baseline="middle" fill="${textFill}" font-size="${fontSize}">${this.escapeHtml(this.cfg.segments[i].label)}</text>`;
        wedges += `<g transform="rotate(${i * theta})">`
          + `<path d="${path}" fill="${fill}" stroke="var(--sdw-wheel-border, #fff)" stroke-width="0.8" stroke-linejoin="round"></path>`
          + `${label}</g>`;
      }

      this.wheelWrap.innerHTML = `
        <svg class="sd-wheel__svg" viewBox="-50 -50 100 100" role="img"
          aria-label="Prize wheel with ${n} segments: ${this.escapeHtml(labelList)}">
          <circle r="49.4" fill="none" stroke="var(--sdw-wheel-border, #fff)" stroke-width="1.2"></circle>
          <g class="sd-wheel__rotor" data-sdw-rotor>${wedges}</g>
          <circle class="sd-wheel__hub" r="7.5" fill="var(--sdw-wheel-border, #fff)"></circle>
          <circle r="3" fill="var(--sdw-pointer, #111)"></circle>
        </svg>`;
      this.rotor = this.wheelWrap.querySelector('[data-sdw-rotor]');
    }

    escapeHtml(str) {
      return String(str || '').replace(/[&<>"']/g, (c) => (
        { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
      ));
    }

    /* Rotation targeting: pointer sits at 12 o'clock; segment i spans [i*θ, (i+1)*θ). */
    rotationFor(index) {
      const n = this.cfg.segments.length;
      const theta = 360 / n;
      const jitter = theta * (0.15 + (0.7 * Math.random()));
      const target = 360 - ((index * theta) + jitter);
      const revolutions = 4 + Math.floor(Math.random() * 2);
      const current = ((this.currentRotation % 360) + 360) % 360;
      let delta = target - current;
      if (delta <= 0) delta += 360;
      return this.currentRotation + (revolutions * 360) + delta;
    }

    rotateToSegment(index, instant) {
      const n = this.cfg.segments.length;
      const theta = 360 / n;
      this.currentRotation = 360 - ((index * theta) + (theta / 2));
      this.rotor.style.transition = instant ? 'transform 0.4s ease' : '';
      this.rotor.style.transform = `rotate(${this.currentRotation}deg)`;
    }

    /* ---------- prize pick + submit ---------- */

    pickSegment() {
      const segs = this.cfg.segments;
      if (this.cfg.forced >= 0 && this.cfg.forced < segs.length) return this.cfg.forced;
      const total = segs.reduce((sum, s) => sum + (s.weight > 0 ? s.weight : 0), 0);
      if (total <= 0) return Math.floor(Math.random() * segs.length);
      let r = Math.random() * total;
      for (let i = 0; i < segs.length; i += 1) {
        if (segs[i].weight > 0) {
          r -= segs[i].weight;
          if (r < 0) return i;
        }
      }
      return segs.length - 1;
    }

    isLosing(seg) {
      return !!seg.losing || !seg.code;
    }

    async handleSubmit(evt) {
      evt.preventDefault();
      if (this.dataset.state === 'submitting' || this.dataset.state === 'spinning') return;
      if (!this.form.reportValidity()) return;

      const index = this.pickSegment();
      const seg = this.cfg.segments[index];
      const prizeTag = this.isLosing(seg) ? 'sd-wheel-prize:none' : `sd-wheel-prize:${seg.handle}`;
      this.tagsInput.value = `${this.tagsInput.dataset.baseValue || this.tagsInput.defaultValue}, ${prizeTag}`;

      // Capture the body BEFORE disabling the fieldset — disabled fields are
      // excluded from FormData and the email would silently go missing.
      const body = new URLSearchParams(new FormData(this.form));

      this.errorEl.hidden = true;
      this.setState('submitting');
      this.fieldset.disabled = true;
      if (!this.designMode) this.writeState({ spins: this.readState().spins + 1 });

      // Test hook: ?sd-wheel-test-challenge=1 forces the captcha fallback path.
      const forceChallenge = /[?&]sd-wheel-test-challenge=1/.test(window.location.search);

      if (this.designMode) {
        // Editor preview: never POST real signups from the customizer.
        this.track('wheel_email_submit');
        this.spin(index);
        return;
      }

      try {
        let challenged = forceChallenge;
        if (!challenged) {
          const resp = await fetch(this.form.action, {
            method: 'POST',
            body,
            headers: { Accept: 'text/html' }
          });
          // Shopify bot protection answers with a /challenge redirect or an
          // interstitial under various statuses (403/429/430). Treat ANY non-ok
          // response as "retry as a native full-page submit" — that path lets
          // Shopify run its verification and return_to brings the visitor back.
          challenged = !resp.ok || resp.url.indexOf('/challenge') !== -1;
        }

        if (challenged) {
          this.challengeFallback(index);
          return;
        }

        this.track('wheel_email_submit');
        this.spin(index);
      } catch (e) {
        console.error('sd-wheel: submit failed', e);
        this.fieldset.disabled = false;
        this.setState('open');
        if (!this.designMode) this.writeState({ spins: Math.max(0, this.readState().spins - 1) });
        this.errorEl.textContent = 'Something went wrong — please try again.';
        this.errorEl.hidden = false;
      }
    }

    /* Shopify served its captcha page: do a real form submit through it and
       resume the spin when the visitor lands back here. */
    challengeFallback(index) {
      storage.session(`pending:${this.cfg.sectionId}`, { i: index, at: Date.now() });
      let returnTo = this.form.querySelector('input[name="return_to"]');
      if (!returnTo) {
        returnTo = document.createElement('input');
        returnTo.type = 'hidden';
        returnTo.name = 'return_to';
        this.form.appendChild(returnTo);
      }
      const sep = window.location.pathname.indexOf('?') === -1 ? '?' : '&';
      returnTo.value = `${window.location.pathname}${sep}sd-wheel=resume`;
      this.fieldset.disabled = false;
      this.form.submit();
    }

    resumeFromChallenge() {
      if (!/[?&]sd-wheel=resume/.test(window.location.search)) return false;
      const pending = storage.session(`pending:${this.cfg.sectionId}`);
      storage.session(`pending:${this.cfg.sectionId}`, null);
      const url = window.location.pathname + window.location.hash;
      window.history.replaceState({}, '', url);
      if (!pending || (Date.now() - pending.at) > 9e5) return false;
      this.open(null, 'challenge-resume');
      this.setState('spinning');
      this.fieldset.disabled = true;
      this.track('wheel_email_submit');
      setTimeout(() => this.spin(pending.i), 600);
      return true;
    }

    /* ---------- spin + result ---------- */

    spin(index) {
      const seg = this.cfg.segments[index];
      this.setState('spinning');
      this.track('wheel_spin');

      // Persist the win at spin start so an interrupted animation still honours it.
      if (!this.isLosing(seg) && !this.designMode) {
        this.writeState({ won: { code: seg.code, label: seg.label, handle: seg.handle, at: Date.now() } });
      }

      const duration = REDUCED_MOTION.matches ? 0.3 : this.cfg.spinDuration;
      const target = this.rotationFor(index);
      if (this.cfg.tick && !REDUCED_MOTION.matches) this.startTicker();

      const done = () => {
        clearTimeout(this.spinFallbackTimer);
        this.rotor.removeEventListener('transitionend', done);
        this.stopTicker();
        this.currentRotation = target;
        this.showResult(seg, { code: seg.code, label: seg.label, handle: seg.handle }, false);
      };
      this.rotor.addEventListener('transitionend', done);
      this.spinFallbackTimer = setTimeout(done, (duration * 1000) + 400);

      requestAnimationFrame(() => {
        this.rotor.style.transition = `transform ${duration}s cubic-bezier(0.12, 0.84, 0.18, 1)`;
        requestAnimationFrame(() => {
          this.rotor.style.transform = `rotate(${target}deg)`;
        });
      });
    }

    showResult(seg, won, restored) {
      const losing = !seg || this.isLosing(seg);

      if (losing) {
        const spins = this.designMode ? 0 : this.readState().spins;
        const retry = this.querySelector('[data-sdw-retry]');
        if (retry) retry.hidden = this.designMode ? false : spins >= this.cfg.maxSpins;
        this.setState('lose');
        this.announce(this.querySelector('[data-panel="lose"] .sd-wheel__heading').textContent);
        if (!restored) this.track('wheel_result', { wheel_outcome: 'lose' });
        if (this.fieldset) this.fieldset.disabled = false;
        return;
      }

      this.querySelector('[data-sdw-code]').textContent = won.code;
      this.querySelector('[data-sdw-prize-label]').textContent = won.label;
      const msgEl = this.querySelector('[data-sdw-prize-message]');
      if (msgEl) {
        msgEl.textContent = seg.message || '';
        msgEl.hidden = !seg.message;
      }
      const apply = this.querySelector('[data-sdw-apply]');
      if (apply) {
        apply.href = `/discount/${encodeURIComponent(won.code)}?redirect=${encodeURIComponent(this.cfg.applyRedirect || '/')}`;
      }
      this.setState('win');
      this.announce(`${won.label}. Your code is ${won.code}`);
      if (!restored) {
        this.track('wheel_result', { wheel_outcome: 'win', wheel_prize: won.handle });
        if (this.cfg.confetti && !REDUCED_MOTION.matches) this.burstConfetti();
      }
    }

    announce(text) {
      // Delay so screen readers announce once, after the state swap settles.
      setTimeout(() => { this.liveEl.textContent = text; }, 400);
    }

    copyCode() {
      const code = this.querySelector('[data-sdw-code]').textContent;
      const label = this.querySelector('[data-sdw-copy-label]');
      const flash = () => {
        const original = label.textContent;
        label.textContent = 'Copied!';
        setTimeout(() => { label.textContent = original; }, 2000);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code).then(flash).catch(() => this.copyFallback(code, flash));
      } else {
        this.copyFallback(code, flash);
      }
      this.track('wheel_code_copy');
    }

    copyFallback(code, onDone) {
      const ta = document.createElement('textarea');
      ta.value = code;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (e) { /* best effort */ }
      document.body.removeChild(ta);
      onDone();
    }

    segmentByHandle(handle) {
      return this.cfg.segments.find((s) => s.handle === handle) || null;
    }

    /* ---------- tick sound (off by default) ---------- */

    startTicker() {
      try {
        this.audioCtx = this.audioCtx || new (window.AudioContext || window.webkitAudioContext)();
      } catch (e) {
        return;
      }
      const theta = 360 / this.cfg.segments.length;
      let lastIndex = Math.floor(this.readRotorAngle() / theta);
      const loop = () => {
        if (this.dataset.state !== 'spinning') return;
        const idx = Math.floor(this.readRotorAngle() / theta);
        if (idx !== lastIndex) {
          lastIndex = idx;
          this.click();
        }
        this.tickRaf = requestAnimationFrame(loop);
      };
      this.tickRaf = requestAnimationFrame(loop);
    }

    stopTicker() {
      cancelAnimationFrame(this.tickRaf);
    }

    readRotorAngle() {
      const t = getComputedStyle(this.rotor).transform;
      if (!t || t === 'none') return 0;
      const [a, b] = t.match(/-?[\d.]+/g).map(Number);
      const deg = (Math.atan2(b, a) * 180) / Math.PI;
      return ((deg % 360) + 360) % 360;
    }

    click() {
      const ctx = this.audioCtx;
      if (!ctx) return;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'square';
      osc.frequency.value = 2200;
      gain.gain.setValueAtTime(0.06, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.04);
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.045);
    }

    /* ---------- confetti (no library) ---------- */

    burstConfetti() {
      const canvas = document.createElement('canvas');
      canvas.className = 'sd-wheel__confetti';
      this.window.appendChild(canvas);
      const ctx = canvas.getContext('2d');
      const rect = this.window.getBoundingClientRect();
      canvas.width = rect.width * devicePixelRatio;
      canvas.height = rect.height * devicePixelRatio;
      ctx.scale(devicePixelRatio, devicePixelRatio);

      const colors = this.cfg.segments.map((_, i) => this.segmentColor(i));
      const parts = Array.from({ length: 120 }, () => ({
        x: rect.width / 2 + (Math.random() - 0.5) * rect.width * 0.3,
        y: rect.height * 0.35,
        vx: (Math.random() - 0.5) * 9,
        vy: -4 - Math.random() * 7,
        w: 4 + Math.random() * 5,
        h: 6 + Math.random() * 6,
        rot: Math.random() * Math.PI,
        vr: (Math.random() - 0.5) * 0.3,
        color: colors[Math.floor(Math.random() * colors.length)]
      }));

      const start = performance.now();
      const frame = (now) => {
        const t = (now - start) / 2000;
        ctx.clearRect(0, 0, rect.width, rect.height);
        if (t >= 1) {
          canvas.remove();
          return;
        }
        ctx.globalAlpha = 1 - Math.max(0, (t - 0.6) / 0.4);
        parts.forEach((p) => {
          p.vy += 0.18;
          p.vx *= 0.99;
          p.x += p.vx;
          p.y += p.vy;
          p.rot += p.vr;
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rot);
          ctx.fillStyle = p.color;
          ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
          ctx.restore();
        });
        requestAnimationFrame(frame);
      };
      requestAnimationFrame(frame);
    }
  }

  customElements.define('sd-wheel-popup', SdWheelPopup);
})();
